import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  TrendingUp, 
  Users, 
  School, 
  FileText, 
  DollarSign,
} from 'lucide-react'
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  PieChart as RechartsPieChart, 
  Pie, 
  Cell,
  AreaChart,
  Area
} from 'recharts'

interface AnalyticsData {
  totalUsers: number
  totalSchools: number
  totalApplications: number
  totalRevenue: number
  monthlyGrowth: number
  userGrowth: number
  applicationGrowth: number
  revenueGrowth: number
}

const Analytics = () => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData>({
    totalUsers: 0,
    totalSchools: 0,
    totalApplications: 0,
    totalRevenue: 0,
    monthlyGrowth: 0,
    userGrowth: 0,
    applicationGrowth: 0,
    revenueGrowth: 0
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchAnalyticsData()
  }, [])

  const fetchAnalyticsData = async () => {
    try {
      // Mock data for now - replace with actual API call
      setAnalyticsData({
        totalUsers: 1247,
        totalSchools: 89,
        totalApplications: 456,
        totalRevenue: 45600,
        monthlyGrowth: 12.5,
        userGrowth: 8.3,
        applicationGrowth: 15.2,
        revenueGrowth: 18.7
      })
    } catch (error) {
      console.error('Error fetching analytics data:', error)
    } finally {
      setLoading(false)
    }
  }

  const userGrowthData = [
    { name: 'Jan', users: 400, applications: 240, revenue: 12000 },
    { name: 'Feb', users: 300, applications: 139, revenue: 8500 },
    { name: 'Mar', users: 200, applications: 980, revenue: 15000 },
    { name: 'Apr', users: 278, applications: 390, revenue: 11000 },
    { name: 'May', users: 189, applications: 480, revenue: 13500 },
    { name: 'Jun', users: 239, applications: 380, revenue: 12500 },
    { name: 'Jul', users: 349, applications: 430, revenue: 18000 },
  ]

  const schoolStatusData = [
    { name: 'Verified', value: 67, color: '#10b981' },
    { name: 'Pending', value: 15, color: '#f59e0b' },
    { name: 'Rejected', value: 7, color: '#ef4444' },
  ]

  const applicationStatusData = [
    { name: 'Approved', value: 234, color: '#10b981' },
    { name: 'Pending', value: 23, color: '#f59e0b' },
    { name: 'Rejected', value: 45, color: '#ef4444' },
    { name: 'Under Review', value: 154, color: '#3b82f6' },
  ]

  const revenueData = [
    { name: 'Q1', revenue: 45000, applications: 120 },
    { name: 'Q2', revenue: 52000, applications: 140 },
    { name: 'Q3', revenue: 48000, applications: 130 },
    { name: 'Q4', revenue: 61000, applications: 160 },
  ]

  const topSchoolsData = [
    { name: 'Bliss Yoga Rishikesh', applications: 45, revenue: 12000 },
    { name: 'Himalayan Yoga Academy', applications: 38, revenue: 9500 },
    { name: 'Sacred Space Yoga', applications: 32, revenue: 8000 },
    { name: 'Mountain View Yoga', applications: 28, revenue: 7000 },
    { name: 'Peaceful Yoga Center', applications: 25, revenue: 6500 },
  ]

  const statCards = [
    {
      title: 'Total Users',
      value: analyticsData.totalUsers.toLocaleString(),
      change: `+${analyticsData.userGrowth}%`,
      changeType: 'positive' as const,
      icon: Users,
      color: 'bg-blue-500'
    },
    {
      title: 'Yoga Schools',
      value: analyticsData.totalSchools.toLocaleString(),
      change: '+12.3%',
      changeType: 'positive' as const,
      icon: School,
      color: 'bg-green-500'
    },
    {
      title: 'Applications',
      value: analyticsData.totalApplications.toLocaleString(),
      change: `+${analyticsData.applicationGrowth}%`,
      changeType: 'positive' as const,
      icon: FileText,
      color: 'bg-purple-500'
    },
    {
      title: 'Total Revenue',
      value: `$${analyticsData.totalRevenue.toLocaleString()}`,
      change: `+${analyticsData.revenueGrowth}%`,
      changeType: 'positive' as const,
      icon: DollarSign,
      color: 'bg-yellow-500'
    }
  ]

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Analytics</h1>
        <p className="text-gray-600">Platform performance and insights</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card, index) => (
          <motion.div
            key={card.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200"
          >
            <div className="">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{card.title}</p>
                  <p className="text-2xl font-bold text-gray-900 mb-2">{card.value}</p>
                </div>
                <div className={`p-3 rounded-lg ${card.color}`}>
                  <card.icon className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="mt-4 flex items-center">
                <TrendingUp className={`h-4 w-4 ${
                  card.changeType === 'positive' ? 'text-green-500' : 'text-red-500'
                }`} />
                <span className={`text-sm font-medium ml-1 ${
                  card.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {card.change}
                </span>
                <span className="text-sm text-gray-500 ml-1">from last month</span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Growth Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200"
        >
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-900">User & Application Growth</h3>
            <p className="text-sm text-gray-600">Monthly trends over the last 7 months</p>
          </div>
          <div className="">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={userGrowthData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="users" stroke="#3b82f6" strokeWidth={2} />
                <Line type="monotone" dataKey="applications" stroke="#10b981" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Revenue Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200"
        >
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-900">Revenue Trends</h3>
            <p className="text-sm text-gray-600">Quarterly revenue and application data</p>
          </div>
          <div className="">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Area type="monotone" dataKey="revenue" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.3} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>

      {/* School Status Chart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200"
      >
        <div className="card-header">
          <h3 className="text-lg font-semibold text-gray-900">School Verification Status</h3>
          <p className="text-sm text-gray-600">Current status of yoga school verifications</p>
        </div>
        <div className="">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <ResponsiveContainer width="100%" height={300}>
                <RechartsPieChart>
                  <Pie
                    data={schoolStatusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {schoolStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-4">
              {schoolStatusData.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div 
                      className="w-4 h-4 rounded-full" 
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-sm text-ivory-700">{item.name}</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Application Status Chart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200"
      >
        <div className="card-header">
          <h3 className="text-lg font-semibold text-gray-900">Application Status Distribution</h3>
          <p className="text-sm text-gray-600">Breakdown of application statuses</p>
        </div>
        <div className="">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={applicationStatusData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </motion.div>

      {/* Top Schools */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200"
      >
        <div className="card-header">
          <h3 className="text-lg font-semibold text-gray-900">Top Performing Schools</h3>
          <p className="text-sm text-gray-600">Schools with highest applications and revenue</p>
        </div>
        <div className="">
          <div className="space-y-4">
            {topSchoolsData.map((school, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-4">
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-primary-600">{index + 1}</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{school.name}</p>
                    <p className="text-sm text-gray-600">{school.applications} applications</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium text-gray-900">${school.revenue.toLocaleString()}</p>
                  <p className="text-sm text-gray-600">Revenue</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Recent Activity */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9 }}
        className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200"
      >
        <div className="card-header">
          <h3 className="text-lg font-semibold text-gray-900">Platform Activity</h3>
          <p className="text-sm text-gray-600">Recent platform activities and trends</p>
        </div>
        <div className="">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-primary-600 mb-2">98.5%</div>
              <p className="text-sm text-gray-600">Uptime</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600 mb-2">2.3s</div>
              <p className="text-sm text-gray-600">Avg Response Time</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600 mb-2">4.8/5</div>
              <p className="text-sm text-gray-600">User Satisfaction</p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default Analytics


